import React, { FormEvent, useContext, useEffect, useState } from 'react';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { PasswordChecker } from '../passwordChecker';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { UserContext } from '../../../context/authContext';
import { useAlert } from '../../../context/alertContext';
import { Modal } from '@cloudscape-design/components';
import { buttonStyle } from '../style/styleSet';
import { TermsView } from './termsView';

/**
 * Drop-in Signup Page built with Tailwind CSS
 * -------------------------------------------
 * - Email + Password fields
 * - Show/Hide password toggle
 * - Minimal client-side validation (HTML5)
 * - Accessible labels, focus states, and error text placeholders
 * - Contemporary card-on-gradient design with subtle motion
 */

export default function LoginPageNew() {
  const { getSignIn, token, cognitoChallenge, passwordUserError } =
    useContext(UserContext);
  const { setAlert } = useAlert();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [remember, setRemember] = useState<boolean>(false);
  const [newpassword1, setNewPassword1] = useState<string>('');
  const [newpassword2, setNewPassword2] = useState<string>('');
  const [showPwd, setShowPwd] = useState(false);
  // const [showLostPassword, setShowLostPassword] = useState<boolean>(false);
  const [showResetMessage, setShowResetMessage] = useState<string>('');
  const [resetCode, setResetCode] = useState<string>('');

  //terms and privacy

  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // const [showResetCode, setShowResetCode] = useState<boolean>(false);
  const navigate = useNavigate();
  const validatePassword = (pwd: string) => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    return regex.test(pwd);
  };
  useEffect(() => {
    if (newpassword1 === newpassword2) {
      setPassword(newpassword1);
    }
  }, [newpassword1, newpassword2]);
  const loginButtonDisabled = () => {
    if (!cognitoChallenge) {
      return email === '' || password === '';
    }

    if (cognitoChallenge) {
      return (
        newpassword1 !== newpassword2 ||
        newpassword1 === '' ||
        newpassword2 === ''
      );
    }
  };

  const location = useLocation();

  return (
    <>
      {!token ? (
        <div className="min-h-screen w-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#62d29e]/40 via-[#62d29e]/20 to-white dark:from-slate-950 dark:via-slate-950 dark:to-black flex items-center justify-center p-6">
          {/* Subtle grid overlay */}
          <div className="pointer-events-none fixed inset-0 opacity-[0.04] [background-image:linear-gradient(to_right,theme(colors.slate.500/.35)_1px,transparent_1px),linear-gradient(to_bottom,theme(colors.slate.500/.35)_1px,transparent_1px)] [background-size:32px_32px]" />

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="relative z-10 w-full max-w-md"
          >
            <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white/80 dark:bg-slate-900/70 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)]">
              {/* Header */}
              <div className="px-8 pt-8 pb-4 text-center">
                <img
                  src="/korulogo.png"
                  alt="Koru Logo"
                  className="mx-auto mb-4 h-12 w-12 object-contain"
                  loading="eager"
                  decoding="async"
                />
                <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
                  {'Login'}
                </h1>
                {/* <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Start your 14-day free trial. No credit card required.
            </p> */}
              </div>

              {/* Form */}
              <form
                className="px-8 pb-8 pt-4 space-y-5"
                onSubmit={(e) => e.preventDefault()}
              >
                {/* Regular Login */}
                {!cognitoChallenge && (
                  <>
                    <div>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                      >
                        Email
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Mail
                            className="h-4 w-4 text-slate-400"
                            aria-hidden="true"
                          />
                        </div>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 pl-10 pr-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                        />
                      </div>
                    </div>
                    {/* Password */}

                    <div>
                      <div className="flex items-center justify-between">
                        <label
                          htmlFor="password"
                          className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                        >
                          Password
                        </label>
                        {/* <span className="text-xs text-slate-500 dark:text-slate-400">
                  Min 8 characters
                  </span> */}
                      </div>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Lock
                            className="h-4 w-4 text-slate-400"
                            aria-hidden="true"
                          />
                        </div>
                        <input
                          id="password"
                          name="password"
                          type={showPwd ? 'text' : 'password'}
                          required
                          value={password}
                          autoComplete="password"
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 pl-10 pr-10 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPwd((s) => !s)}
                          aria-label={
                            showPwd ? 'Hide password' : 'Show password'
                          }
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                        >
                          {showPwd ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      <p
                        className="text-sm p-2 hover:cursor-pointer"
                        onClick={() => navigate('/reset-password')}
                      >
                        Forgot password?
                      </p>
                    </div>
                  </>
                )}
                {/* If there is a cognito challenge */}
                {cognitoChallenge && (
                  <>
                    <div>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                      >
                        Email
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Mail
                            className="h-4 w-4 text-slate-400"
                            aria-hidden="true"
                          />
                        </div>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 pl-10 pr-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                        />
                      </div>
                    </div>
                    {/* Email */}

                    {/* Password */}
                    <div>
                      <div className="flex items-center justify-between">
                        <label
                          htmlFor="newpassword1"
                          className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                        >
                          New Password
                        </label>
                      </div>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Lock
                            className="h-4 w-4 text-slate-400"
                            aria-hidden="true"
                          />
                        </div>
                        <input
                          id="newpassword1"
                          name="newpassword1"
                          type={showPwd ? 'text' : 'password'}
                          required
                          value={newpassword1}
                          onChange={(e) => setNewPassword1(e.target.value)}
                          placeholder="••••••••"
                          className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 pl-10 pr-10 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPwd((s) => !s)}
                          aria-label={
                            showPwd ? 'Hide password' : 'Show password'
                          }
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                        >
                          {showPwd ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    {/* New Password */}
                    <div>
                      <div className="flex items-center justify-between">
                        <label
                          htmlFor="newpassword2"
                          className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                        >
                          Confirm new password
                        </label>
                        {/* <span className="text-xs text-slate-500 dark:text-slate-400">
                  Min 8 characters
                  </span> */}
                      </div>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Lock
                            className="h-4 w-4 text-slate-400"
                            aria-hidden="true"
                          />
                        </div>
                        <input
                          id="newpassword2"
                          name="newpassword2"
                          type={showPwd ? 'text' : 'password'}
                          required
                          value={newpassword2}
                          onChange={(e) => setNewPassword2(e.target.value)}
                          placeholder="••••••••"
                          className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 pl-10 pr-10 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPwd((s) => !s)}
                          aria-label={
                            showPwd ? 'Hide password' : 'Show password'
                          }
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                        >
                          {showPwd ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
                {/* if there is a lost password request */}

                {/* end reset password */}

                {/* Submit */}
                <button
                  onClick={() => {
                    getSignIn(email, password, false);
                  }}
                  disabled={loginButtonDisabled()}
                  className={buttonStyle}
                >
                  {/* {!cognitoChallenge ? 'Login In' : 'Update Password'} */}
                  {(() => {
                    switch (true) {
                      case cognitoChallenge:
                        return 'Update Password';
                      case !cognitoChallenge:
                        return 'Login In';
                    }
                  })()}
                </button>

                {/* Error placeholder */}
                {passwordUserError && (
                  <p
                    className="text-sm text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    {'Incorrect username or password'}
                  </p>
                )}

                {/* Fine print */}
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                  By signing in, you agree to our{' '}
                  <span
                    onClick={() => setShowTerms(true)}
                    className="underline underline-offset-2 hover:text-slate-700 dark:hover:text-slate-200 hover:cursor-pointer"
                  >
                    Terms
                  </span>{' '}
                  and{' '}
                  <span
                    onClick={() => setShowPrivacy(true)}
                    className="underline underline-offset-2 hover:text-slate-700 dark:hover:text-slate-200 hover:cursor-pointer"
                  >
                    Privacy Policy
                  </span>
                  .
                </p>

                <div className="text-center text-sm text-slate-600 dark:text-slate-300">
                  Don't have an account?{' '}
                  <a
                    href={`${process.env.REACT_APP_BASE_URL}/signup`}
                    className="font-medium text-slate-900 dark:text-white underline-offset-4 hover:underline"
                  >
                    Sign Up
                  </a>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      ) : (
        navigate(
          location.pathname && location.pathname !== '/login'
            ? `${location.state.pathname}${location.state.search}`
            : '/*'
        )
      )}
      <TermsView
        showPrivacy={showPrivacy}
        showTerms={showTerms}
        setShowPrivacy={setShowPrivacy}
        setShowTerms={setShowTerms}
      />
    </>
  );
}
